import { AssessmentCompletionGuard } from "@/components/assessment/AssessmentCompletionGuard";
import { AssessmentHeader } from "@/components/assessment/AssessmentHeader";
import { AssessmentLoader } from "@/components/assessment/AssessmentLoader";
import { AssessmentStartScreen } from "@/components/assessment/AssessmentStartScreen";
import { GroupProgressBreadcrumb } from "@/components/assessment/GroupProgressBreadcrumb";
import { GroupTransitionDialog } from "@/components/assessment/GroupTransitionDialog";
import { IPValidationGuard } from "@/components/assessment/IPValidationGuard";
import { GroupQuestionsDisplay } from "@/components/assessment/GroupQuestionsDisplay";
import { GroupAssessmentNavigation } from "@/components/assessment/GroupAssessmentNavigation";
import { TimeWarning } from "@/components/assessment/TimeWarning";
import { TimeValidationGuard } from "@/components/assessment/TimeValidationGuard";
import { useToast } from "@/hooks/use-toast";
import { useAssessmentData } from "@/hooks/useAssessmentData";
import { useAssessmentSession } from "@/hooks/useAssessmentSession";
import { useGroupResponses } from "@/hooks/useGroupResponses";
import { ServerTimeProvider } from "@/hooks/useServerTime";
import { useSearchParams } from "react-router-dom";
import { getClientIP } from "@/utils/assessmentValidation";
import { useCallback, useEffect, useState } from "react";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { useQueryClient } from "@tanstack/react-query";
import { getSafeErrorMessage } from "@/utils/safeErrorMessage";

function PublicAssessmentContent() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [clientIP, setClientIP] = useState<string>("");
  const [hasConnectivityIssue, setHasConnectivityIssue] = useState(false);
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);

  // Get client IP address on mount
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const ip = await getClientIP();
        setClientIP(ip);
      } catch (error) {
        console.error("Failed to get client IP:", error);
        setClientIP("unknown");
      }
    };
    fetchIP();
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      setHasConnectivityIssue(true);
    };

    const handleOnline = () => {
      // connection restored
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [toast]);

  const handleAutoSubmitAttempt = useCallback(() => {
    if (hasConnectivityIssue || !navigator.onLine) {
      return;
    }

    setPendingAutoSubmit(true);
  }, [hasConnectivityIssue, toast]);

  // Fetch real assessment data using the token
  const {
    candidate,
    college,
    jobPosition,
    questions,
    questionGroups,
    existingResponses,
    existingEvaluations,
    isLoading,
    error,
  } = useAssessmentData({ token });

  // Group-level response management
  const {
    responses,
    handleAnswerChange,
    handleOptionSelect,
    saveGroupResponses,
    isSavingResponses,
  } = useGroupResponses({
    candidateId: candidate?.id || null,
    questions: questions || [],
    clientIP,
  });

  // Assessment session management
  const {
    timeRemaining,
    showGroupTransition,
    currentGroup,
    nextGroup,
    handleNextGroup,
    handlePreviousGroup,
    handleContinueToNextGroup,
    handleSubmitAssessment,
    setShowGroupTransition,
    canGoPreviousGroup,
    canGoNextGroup,
    isSubmitting,
    isLastGroup,
    currentGroupIndex,
    totalGroups,
    isAssessmentNotStarted,
    startAssessment,
  } = useAssessmentSession({
    candidate,
    college,
    questions: questions || [],
    questionGroups: questionGroups || [],
    existingResponses: existingResponses || [],
    clientIP, // Pass clientIP to the hook
    onSubmitComplete: async () => {
      toast({
        title: "Assessment Submitted",
        description: "Your assessment has been submitted successfully.",
      });

      // Invoke Supabase function to evaluate assessment (fire-and-forget)
      invokeEdgeFunction("evaluate-assessment", {
        body: { candidateId: candidate.id },
      }).catch((err) => {
        console.error("Assessment evaluation error:", err);
      });
    },
    onGroupComplete: async () => {
      // Invalidate candidate query to refetch updated status after group change
      await queryClient.invalidateQueries({ queryKey: ["candidate-by-token"] });
    },
    onAutoSubmit: handleAutoSubmitAttempt,
  });

  const [isStartingAssessment, setIsStartingAssessment] = useState(false);
  const [startAssessmentError, setStartAssessmentError] = useState<any>(null);

  const handleStartAssessment = useCallback(async () => {
    setIsStartingAssessment(true);
    setStartAssessmentError(null);

    // Request fullscreen on user gesture (before async work)
    try {
      await document.documentElement.requestFullscreen();
    } catch {
      // Fullscreen may be denied (e.g. embedded iframe) — continue anyway
    }

    const result = await startAssessment();

    if (!result.success) {
      setStartAssessmentError(result.error);

      // Show appropriate error message based on error code
      let errorTitle = "Error";
      let errorDescription = "Failed to start assessment. Please try again.";

      if (result.error?.errorCode) {
        switch (result.error.errorCode) {
          case "WRONG_DATE":
            errorTitle = "Assessment Not Available Today";
            errorDescription =
              result.error.error || "Assessment is not scheduled for today.";
            break;
          case "TOO_EARLY":
            errorTitle = "Assessment Not Started Yet";
            errorDescription =
              result.error.error || "Assessment window has not opened yet.";
            break;
          case "TOO_LATE":
            errorTitle = "Assessment Window Closed";
            errorDescription =
              result.error.error || "Assessment window has closed.";
            break;
          case "ALREADY_STARTED":
            errorTitle = "Assessment Already Started";
            errorDescription = "This assessment has already been started.";
            break;
          case "ALREADY_SUBMITTED":
            errorTitle = "Assessment Already Submitted";
            errorDescription = "This assessment has already been submitted.";
            break;
          default:
            errorDescription = result.error.error || errorDescription;
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });

      // Exit fullscreen if start failed
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    }

    setIsStartingAssessment(false);
  }, [startAssessment, toast]);

  // Override handleContinueToNextGroup and handleSubmitAssessment to save responses first
  const handleContinueAndSave = useCallback(async () => {
    const saved = await saveGroupResponses();
    if (saved) {
      handleContinueToNextGroup();
    } else {
      toast({
        title: "Error",
        description: "Failed to save responses. Please try again.",
        variant: "destructive",
      });
    }
  }, [saveGroupResponses, handleContinueToNextGroup, toast]);

  const handleSubmitAndSave = useCallback(
    async (isAutoSubmit = false) => {
      const saved = await saveGroupResponses();

      if (saved) {
        handleSubmitAssessment(isAutoSubmit);
      } else {
        toast({
          title: "Error",
          description: "Failed to save responses. Please try again.",
          variant: "destructive",
        });
      }
    },
    [saveGroupResponses, handleSubmitAssessment, toast]
  );

  const handlePreviousAndSave = useCallback(async () => {
    const saved = await saveGroupResponses();
    if (saved) {
      handlePreviousGroup();
    } else {
      toast({
        title: "Error",
        description: "Failed to save responses. Please try again.",
        variant: "destructive",
      });
    }
  }, [saveGroupResponses, handlePreviousGroup, toast]);

  useEffect(() => {
    if (!pendingAutoSubmit) {
      return;
    }

    setPendingAutoSubmit(false);
    void handleSubmitAndSave(true);
  }, [pendingAutoSubmit, handleSubmitAndSave]);

  if (isLoading) {
    return <AssessmentLoader isLoading={true} />;
  }

  if (error) {
    const safeErrorMessage = getSafeErrorMessage(
      error,
      "Unable to load the assessment. Please verify your link and try again."
    );

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Assessment Error
          </h1>
          <p className="text-gray-600">{safeErrorMessage}</p>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Invalid Assessment Link
          </h1>
          <p className="text-gray-600">
            The assessment link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  if (!college) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            College Not Found
          </h1>
          <p className="text-gray-600">
            The college associated with this assessment could not be found.
            Please contact support.
          </p>
        </div>
      </div>
    );
  }

  // Block candidates who registered in a previous year
  const registrationYear = new Date(candidate.created_at).getFullYear();
  const currentYear = new Date().getFullYear();
  if (registrationYear < currentYear) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center space-y-4">
          <div className="flex justify-center">
            <span className="text-5xl">🚫</span>
          </div>
          <h1 className="text-2xl font-bold text-red-600">
            Not Eligible for Assessment
          </h1>
          <p className="text-gray-600">
            You are not eligible to take this assessment. Only candidates
            registered in <strong>{currentYear}</strong> can participate.
          </p>
          <p className="text-sm text-gray-500">
            Please contact your placement office if you believe this is a
            mistake.
          </p>
        </div>
      </div>
    );
  }

  // Show start screen if assessment not started
  if (isAssessmentNotStarted) {
    return (
      <IPValidationGuard
        allowedIPRanges={candidate?.disable_ip_validation ? null : college?.ip_range || null}
      >
        <TimeValidationGuard college={college} candidate={candidate}>
          <AssessmentStartScreen
            candidate={candidate}
            college={college}
            jobPosition={jobPosition}
            onStartAssessment={handleStartAssessment}
            isStarting={isStartingAssessment}
            error={startAssessmentError}
            onRetryStart={() => setStartAssessmentError(null)}
          />
        </TimeValidationGuard>
      </IPValidationGuard>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <IPValidationGuard
        allowedIPRanges={candidate?.disable_ip_validation ? null : college?.ip_range || null}
      >
        <TimeValidationGuard
          college={college}
          candidate={candidate}
          onTimeExpired={handleAutoSubmitAttempt}
        >
          <AssessmentCompletionGuard
            candidate={candidate}
            existingEvaluations={existingEvaluations || []}
          >
            <div className="container mx-auto px-4 py-8">
              <AssessmentHeader
                candidate={candidate}
                timeRemaining={timeRemaining}
                currentGroup={currentGroup}
                currentGroupIndex={currentGroupIndex}
                totalGroups={totalGroups}
                college={college}
                jobPosition={jobPosition}
                hideTimer={timeRemaining !== null && timeRemaining <= 300}
              />

            <div className="mt-6">
              {timeRemaining !== null && timeRemaining <= 300 && (
                <TimeWarning timeRemaining={timeRemaining} />
              )}

              {/* Group Progress Breadcrumb */}
              {questionGroups && questionGroups.length > 1 && (
                <GroupProgressBreadcrumb
                  questionGroups={questionGroups}
                  currentGroupIndex={currentGroupIndex}
                />
              )}

              {/* Group Questions Display */}
              {questions && questions.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <GroupQuestionsDisplay
                    questions={questions}
                    responses={responses}
                    onAnswerChange={handleAnswerChange}
                    onOptionSelect={handleOptionSelect}
                    groupName={currentGroup?.name}
                  />
                </div>
              )}

              {/* Group Navigation */}
              <GroupAssessmentNavigation
                currentGroupIndex={currentGroupIndex}
                totalGroups={totalGroups}
                canGoPreviousGroup={canGoPreviousGroup}
                canGoNextGroup={canGoNextGroup}
                onPreviousGroup={handlePreviousAndSave}
                onNextGroup={handleNextGroup}
                onSubmit={handleSubmitAndSave}
                isLastGroup={isLastGroup}
                isSubmitting={isSubmitting}
                isSavingResponses={isSavingResponses}
                currentGroupName={currentGroup?.name}
                nextGroupName={nextGroup?.name}
              />

              {/* Group Transition Dialog */}
              <GroupTransitionDialog
                isOpen={showGroupTransition}
                onClose={() => setShowGroupTransition(false)}
                completedGroup={currentGroup}
                nextGroup={nextGroup}
                onContinue={
                  isLastGroup ? handleSubmitAndSave : handleContinueAndSave
                }
                isLastGroup={isLastGroup}
                isSavingResponses={isSavingResponses}
              />
            </div>
          </div>
        </AssessmentCompletionGuard>
      </TimeValidationGuard>
      </IPValidationGuard>
    </div>
  );
}

export default function PublicAssessment() {
  return (
    <ServerTimeProvider>
      <PublicAssessmentContent />
    </ServerTimeProvider>
  );
}
